/**
 * Kotlin type mapper - Functional implementation with dependency injection
 * Maps TypeScript types to Kotlin types with support for different contexts
 */

import { isAnyObjectType } from "../../utils/type-helpers";

export interface KotlinTypeMappingOptions {
  /**
   * Module name for prefixing generated types
   */
  moduleName?: string;
  /**
   * Whether to use React Native bridge types
   */
  useBridgeTypes?: boolean;
  /**
   * Whether to generate nullable types for optionals
   */
  useNullableTypes?: boolean;
}

export interface KotlinTypeMapper {
  /**
   * Maps TypeScript types to Kotlin types (for data classes)
   */
  mapTypeScriptToKotlin(tsType: string): string;

  /**
   * Enhanced Kotlin type mapping that recognizes interface types
   */
  mapTypeScriptToKotlinWithInterfaces(type: string): string;

  /**
   * Maps TypeScript types to React Native bridge types for Kotlin
   */
  mapTypeScriptToReactNativeKotlin(
    tsType: string,
    isReturnType?: boolean,
    isOptional?: boolean
  ): string;

  /**
   * Gets default Kotlin return value for a type
   */
  getDefaultKotlinReturnValue(tsType: string): string;

  /**
   * Checks if a string matches PascalCase pattern (used for interface names)
   */
  isPascalCase(str: string): boolean;

  /**
   * Checks if a type is a primitive type
   */
  isPrimitiveType(tsType: string): boolean;
}

/**
 * Creates a Kotlin type mapper with the given options
 */
export function createKotlinTypeMapper(
  options: KotlinTypeMappingOptions = {}
): KotlinTypeMapper {
  const {
    moduleName,
    useBridgeTypes = false,
    useNullableTypes = true,
  } = options;

  /**
   * Maps TypeScript types to Kotlin types (for data classes)
   */
  function mapTypeScriptToKotlin(tsType: string): string {
    // Handle Promise types
    if (tsType.startsWith("Promise<") && tsType.endsWith(">")) {
      const innerType = tsType.slice(8, -1);
      return mapTypeScriptToKotlin(innerType);
    }

    // Basic type mapping
    switch (tsType) {
      case "string":
        return "String";
      case "number":
        return "Double";
      case "boolean":
        return "Boolean";
      case "void":
        return "Unit";
      case "Any":
        return "Any";
      case "any":
        return "Any";
      default:
        // Array types
        if (tsType.includes("[]")) {
          const elementType = tsType.replace("[]", "");
          return `List<${mapTypeScriptToKotlin(elementType)}>`;
        }
        // Object types - these should have been converted to interface names by parser
        if (tsType.startsWith("{") && tsType.endsWith("}")) {
          // This should rarely happen now, as parser converts nested objects to interfaces
          console.warn(
            `[Android] Object literal type not converted to interface: ${tsType}`
          );
          return "Any";
        }
        // For concrete interface names (User, UserResult, etc.), use them directly
        // This preserves type safety instead of converting everything to Any
        return tsType;
    }
  }

  /**
   * Enhanced Kotlin type mapping that recognizes interface types
   */
  function mapTypeScriptToKotlinWithInterfaces(type: string): string {
    // Handle Promise types - unwrap first
    if (type.startsWith("Promise<") && type.endsWith(">")) {
      const innerType = type.slice(8, -1);
      return mapTypeScriptToKotlinWithInterfaces(innerType);
    }

    // Check for explicit Any type first
    if (type === "Any" || type === "any") {
      return "Any";
    }

    // Check for special generic object types (AnyObject, {})
    if (isAnyObjectType(type)) {
      return "WritableMap";
    }

    // Handle basic types first
    const basicMapping = mapTypeScriptToKotlin(type);
    if (basicMapping !== "Any") {
      return basicMapping;
    }

    // If it's a PascalCase type (likely an interface), keep it as is
    if (isPascalCase(type)) {
      return type;
    }

    return "Any";
  }

  /**
   * Maps TypeScript types to React Native bridge types for Kotlin
   */
  function mapTypeScriptToReactNativeKotlin(
    tsType: string,
    isReturnType: boolean = false,
    isOptional: boolean = false
  ): string {
    // Handle Promise types
    if (tsType.startsWith("Promise<") && tsType.endsWith(">")) {
      return "Unit"; // async functions return Unit in Kotlin bridge
    }

    // Handle callback/function types
    if (tsType === "callback") {
      return "Callback";
    }

    // Basic type mapping to React Native bridge types
    let kotlinType: string;
    switch (tsType) {
      case "string":
        kotlinType = "String";
        break;
      case "number":
        kotlinType = "Double";
        break;
      case "boolean":
        kotlinType = "Boolean";
        break;
      case "void":
        kotlinType = "Unit";
        break;
      case "Any":
        kotlinType = "Any";
        break;
      case "any":
        kotlinType = "Any";
        break;
      case "string[]":
        kotlinType = isReturnType ? "WritableArray" : "ReadableArray";
        break;
      default:
        // Array types
        if (tsType.includes("[]")) {
          kotlinType = isReturnType ? "WritableArray" : "ReadableArray";
        }
        // Object types
        else if (tsType.startsWith("{") && tsType.endsWith("}")) {
          kotlinType = isReturnType ? "WritableMap" : "ReadableMap";
        }
        // Complex types
        else {
          kotlinType = isReturnType ? "WritableMap" : "ReadableMap";
        }
    }

    // Add nullable type if parameter is optional
    if (isOptional && !isReturnType && kotlinType !== "Unit") {
      kotlinType += "?";
    }

    return kotlinType;
  }

  /**
   * Gets default Kotlin return value for a type
   */
  function getDefaultKotlinReturnValue(tsType: string): string {
    switch (tsType) {
      case "string":
        return '""';
      case "number":
        return "0.0";
      case "boolean":
        return "false";
      case "void":
        return "";
      default:
        if (tsType.includes("[]")) {
          return "WritableNativeArray()";
        }
        if (tsType.startsWith("{") && tsType.endsWith("}")) {
          return "WritableNativeMap()";
        }
        return "null";
    }
  }

  /**
   * Checks if a string matches PascalCase pattern (used for interface names)
   */
  function isPascalCase(str: string): boolean {
    return /^[A-Z][a-zA-Z0-9]*$/.test(str);
  }

  /**
   * Checks if a type is a primitive type
   */
  function isPrimitiveType(tsType: string): boolean {
    // Handle Promise types - extract inner type
    let actualType = tsType;
    if (tsType.startsWith("Promise<") && tsType.endsWith(">")) {
      actualType = tsType.slice(8, -1); // Remove Promise< and >
    }

    // Check if it's a primitive type
    return ["string", "number", "boolean", "void"].includes(actualType);
  }

  return {
    mapTypeScriptToKotlin,
    mapTypeScriptToKotlinWithInterfaces,
    mapTypeScriptToReactNativeKotlin,
    getDefaultKotlinReturnValue,
    isPascalCase,
    isPrimitiveType,
  };
}
